import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Package, Plus, Trash2, ArrowLeft, Save, Printer, Download, Send, X, Check, Ban, ListChecks, Tag } from 'lucide-react';
import CustomerDedupHints from '../../components/CustomerDedupHints';
import QuickAddCustomerButton from '../../components/QuickAddCustomerDialog';
import { toast } from 'sonner';
import { DocHistoryPanel } from '../../components/orders/DocHistoryPanel';
import { DocOverrideActions } from '../../components/orders/DocOverrideActions';
import DocumentChainStrip from '../../components/DocumentChainStrip';
import LockedDocBanner from '../../components/LockedDocBanner';
import CarrierSelect from '../../components/CarrierSelect';
import CommercialInvoiceButton from '../../components/CommercialInvoiceButton';
import { SearchPicker, customerOptions } from '../../components/SearchPicker';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ProductKitDialog, hasKit } from '../../components/products/ProductKitDialog';

const STATUS_META = {
  Draft:    { bg: 'bg-slate-100 text-slate-700 border-slate-200' },
  Issued:   { bg: 'bg-blue-100 text-blue-800 border-blue-200' },
  Shipped:  { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  Closed:   { bg: 'bg-slate-900 text-white border-slate-900' },
  Canceled: { bg: 'bg-red-100 text-red-800 border-red-200' },
};

const newLine = () => ({
  id: `tmp-${Math.random().toString(36).slice(2)}`,
  item_sku: '', bom_id: '', product_type_id: '', product_name: '', product_sku: '', description: '',
  quantity_ordered: 0, quantity_shipped: 0,
  uom: '', lot_or_serial: '', notes: '',
});

const PackingSlipDetailPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const fromSo = params.get('from_so');
  const fromPo = params.get('from_po');
  const dupId = params.get('dup');
  const isNew = !id || id === 'new';
  const [ps, setPs] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [kitFor, setKitFor] = useState(null);
  const [form, setForm] = useState({
    customer_id: '',
    ship_date: new Date().toISOString().slice(0, 10),
    carrier: '', tracking_number: '', freight_terms: '', notes: '',
    ship_from_address: '', package_count: 0, total_weight_lbs: 0, shipping_method: '', customer_po_reference: '',
    lines: [newLine()],
  });
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, it, bm] = await Promise.all([
        api().get('/customers'),
        api().get('/items'),
        api().get('/product-types?level=product'),
      ]);
      setCustomers(c.data || []); setItems(it.data || []); setProducts((bm.data || []).filter(p => p.active_status !== false));
      if (!isNew || dupId) {
        const r = await api().get(`/packing-slips/${isNew ? dupId : id}`);
        if (isNew) toast.success(`Copied from ${r.data.ps_number}. Review, then save the new Draft`);
        setPs(isNew ? null : r.data);
        setForm({
          customer_id: r.data.customer_id,
          ship_date: isNew ? new Date().toISOString().slice(0, 10) : r.data.ship_date,
          carrier: r.data.carrier || '',
          tracking_number: isNew ? '' : (r.data.tracking_number || ''),
          freight_terms: r.data.freight_terms || '',
          notes: r.data.notes || '',
          ship_from_address: r.data.ship_from_address || '',
          package_count: r.data.package_count || 0,
          total_weight_lbs: r.data.total_weight_lbs || 0,
          shipping_method: r.data.shipping_method || '',
          customer_po_reference: r.data.customer_po_reference || '',
          lines: (r.data.lines || []).map(l => ({ ...l })),
        });
      } else if (fromSo) {
        try {
          const r = await api().post(`/packing-slips/from-sales-order/${fromSo}`);
          toast.success(`${r.data.ps_number} draft created from SO ${r.data.source_number_snapshot}`);
          navigate(`/orders/packing/${r.data.id}`, { replace: true });
        } catch (e) { toast.error(e.response?.data?.detail || 'Spawn failed'); navigate('/orders/sales'); }
      } else if (fromPo) {
        try {
          const r = await api().post(`/packing-slips/from-production-order/${fromPo}`);
          toast.success(`${r.data.ps_number} draft created from PO ${r.data.source_number_snapshot}`);
          navigate(`/orders/packing/${r.data.id}`, { replace: true });
        } catch (e) { toast.error(e.response?.data?.detail || 'Spawn failed'); navigate('/orders/production'); }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
  }, [api, id, isNew, fromSo, fromPo, dupId, navigate]);
  useEffect(() => { load(); }, [load]);

  const editable = isNew || (ps && (ps.status === 'Draft' || ps.status === 'Issued'));
  const addLine = () => setForm({ ...form, lines: [...form.lines, newLine()] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const updateLine = (i, patch) => setForm({ ...form, lines: form.lines.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln) });

  const fillLineFromItem = (i, sku) => {
    if (!sku) { updateLine(i, { item_sku: '' }); return; }
    const it = items.find(x => x.item_sku === sku);
    if (!it) return;
    updateLine(i, { item_sku: it.item_sku, bom_id: '', product_type_id: '', product_name: '', product_sku: '', description: it.item_name, uom: it.base_uom || '' });
  };
  const fillLineFromProduct = (i, pid) => {
    if (!pid) { updateLine(i, { product_type_id: '', product_name: '', product_sku: '', bom_id: '' }); return; }
    const p = products.find(x => x.id === pid);
    if (!p) return;
    updateLine(i, { product_type_id: p.id, product_name: p.product_type_name, product_sku: p.product_sku || '', bom_id: '', item_sku: '', description: p.description || p.product_type_name, uom: 'EA' });
    if (hasKit(p)) setKitFor({ index: i, product: p, qty: Number(form.lines[i].quantity_ordered) || 1 });
  };
  const addKitLines = (rows) => {
    const { index, product } = kitFor;
    const added = rows.map(r => ({ ...newLine(), item_sku: r.item?.item_sku || r.sku, description: r.item ? r.item.item_name : r.sku, uom: r.item?.base_uom || 'EA', quantity_ordered: r.quantity, quantity_shipped: r.quantity, notes: `${r.group ? `${r.group} for` : 'Included with'} ${product.product_type_name}` }));
    setForm(f => ({ ...f, lines: [...f.lines.slice(0, index + 1), ...added, ...f.lines.slice(index + 1)] }));
    setKitFor(null);
    toast.success(`${added.length} accessory line(s) added`);
  };
  const itemOptions = items.filter(it => it.item_sku).map(it => ({ value: it.item_sku, label: `${it.item_sku} — ${it.item_name}` }));
  const productOptions = products.map(p => ({ value: p.id, label: p.product_sku ? `${p.product_sku} — ${p.product_type_name}` : p.product_type_name }));

  const save = async () => {
    if (!form.customer_id && !((form.customer_name || '').trim())) { toast.error('Pick a customer or type a name'); return; }
    const valid = (form.lines || []).filter(l => (l.description || '').trim());
    if (!valid.length) { toast.error('Add at least one line item'); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        source_type: isNew ? 'Standalone' : ps.source_type,
        source_id: isNew ? null : ps.source_id,
        ship_date: form.ship_date,
        carrier: form.carrier || null,
        tracking_number: form.tracking_number || null,
        freight_terms: form.freight_terms || null,
        ship_from_address: form.ship_from_address || null,
        package_count: Number(form.package_count || 0),
        total_weight_lbs: Number(form.total_weight_lbs || 0),
        shipping_method: form.shipping_method || null,
        customer_po_reference: form.customer_po_reference || null,
        notes: form.notes || null,
        lines: valid.map(l => ({
          item_sku: l.item_sku || null, bom_id: l.bom_id || null,
          product_type_id: l.product_type_id || null, product_name: l.product_name || null, product_sku: l.product_sku || null,
          description: l.description,
          quantity_ordered: Number(l.quantity_ordered || 0),
          quantity_shipped: Number(l.quantity_shipped || 0),
          uom: l.uom || null, lot_or_serial: l.lot_or_serial || null, notes: l.notes || null,
        })),
      };
      if (isNew) {
        const r = await api().post('/packing-slips', payload);
        toast.success(`${r.data.ps_number} created as Draft`);
        navigate(`/orders/packing/${r.data.id}`, { replace: true });
      } else {
        // On update we only send the fields the server accepts for PUT (no source_type)
        const putPayload = { ...payload };
        delete putPayload.source_type; delete putPayload.source_id;
        const r = await api().put(`/packing-slips/${id}`, putPayload);
        setPs(r.data);
        toast.success(`${r.data.ps_number} saved`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const act = async (action, extra = {}) => {
    setActing(true);
    try {
      const r = await api().post(`/packing-slips/${id}/${action}`, extra);
      setPs(r.data);
      toast.success(`${r.data.ps_number} → ${r.data.status}`);
    } catch (e) { toast.error(e.response?.data?.detail || `${action} failed`); } finally { setActing(false); }
  };


  const printPs = () => {
    const base = api().defaults.baseURL;
    const token = api().defaults.headers?.Authorization;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked'); return; }
    w.document.write('Loading…');
    fetch(`${base}/packing-slips/${id}/print`, { headers: { Authorization: token } })
      .then(r => r.text())
      .then(html => { w.document.open(); w.document.write(html); w.document.close(); })
      .catch(() => { w.close(); toast.error('Print load failed'); });
  };

  const openHtml = (path) => {
    const base = api().defaults.baseURL;
    const token = api().defaults.headers?.Authorization;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked'); return; }
    w.document.write('Loading…');
    fetch(`${base}${path}`, { headers: { Authorization: token } })
      .then(r => { if (!r.ok) throw new Error(); return r.text(); })
      .then(html => { w.document.open(); w.document.write(html); w.document.close(); })
      .catch(() => { w.close(); toast.error('Print load failed'); });
  };
  const printChecklist = () => openHtml(`/packing-slips/${id}/checklist`);
  const [labelBoxes, setLabelBoxes] = useState(null);
  const printLabels = () => { const n = Math.max(1, Math.min(50, parseInt(labelBoxes, 10) || 1)); setLabelBoxes(null); openHtml(`/packing-slips/${id}/shipping-labels?boxes=${n}`); };

  const download = async (fmt) => {
    try {
      const res = await api().get(fmt === 'pdf' ? `/packing-slips/${id}/pdf` : `/packing-slips/${id}/export.${fmt}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: fmt === 'pdf' ? 'application/pdf' : (fmt === 'json' ? 'application/json' : 'text/csv') });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${ps.ps_number}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error('Download failed'); }
  };

  const emailPs = async () => {
    try {
      const r = await api().post(`/packing-slips/${id}/email`);
      toast.success(`Emailed to ${r.data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Email failed'); }
  };

  const statusPill = ps && (
    <Badge className={`${(STATUS_META[ps.status] || STATUS_META.Draft).bg} border rounded-sm text-xs uppercase tracking-wider`} data-testid="ps-status-pill">
      {ps.status}
    </Badge>
  );

  const sourceLink = ps && ps.source_id && ps.source_type !== 'Standalone' && (
    <Button variant="ghost" size="sm" className="rounded-sm text-xs"
      onClick={() => navigate(ps.source_type === 'SO' ? `/orders/sales/${ps.source_id}` : `/orders/production/${ps.source_id}`)}
      data-testid="ps-source-link">
      ⇠ View {ps.source_type === 'SO' ? 'Sales Order' : 'Production Order'} {ps.source_number_snapshot}
    </Button>
  );

  return (
    <div className="space-y-4" data-testid="packing-slip-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders/packing')}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package className="w-6 h-6 text-slate-700" />
              {isNew ? 'New Packing Slip' : ps?.ps_number || 'Loading…'}
              {statusPill}
            </h1>
            {ps && (
              <p className="text-xs text-slate-500 mt-1">
                {ps.customer_name_snapshot}
                {ps.source_number_snapshot && <> · Linked {ps.source_type === 'SO' ? 'SO' : 'PO'}: <strong>{ps.source_number_snapshot}</strong></>}
              </p>
            )}
          </div>
        </div>
        {ps && (
          <div className="flex items-center gap-1 flex-wrap">
            {sourceLink}
            <Button variant="outline" size="sm" onClick={printPs} className="rounded-sm" data-testid="ps-print-btn"><Printer className="w-3.5 h-3.5 mr-1" /> Print</Button>
            <Button variant="outline" size="sm" onClick={printChecklist} className="rounded-sm" data-testid="ps-checklist-btn"><ListChecks className="w-3.5 h-3.5 mr-1" /> Packing Checklist</Button>
            <Popover open={labelBoxes !== null} onOpenChange={(o) => setLabelBoxes(o ? '1' : null)}>
              <PopoverTrigger asChild><Button variant="outline" size="sm" className="rounded-sm" data-testid="ps-labels-btn"><Tag className="w-3.5 h-3.5 mr-1" /> Shipping Labels</Button></PopoverTrigger>
              <PopoverContent align="end" className="w-64 space-y-2" data-testid="ps-labels-popover">
                <Label className="text-xs">How many boxes?</Label>
                <Input type="number" min="1" max="50" value={labelBoxes || ''} onChange={(e) => setLabelBoxes(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && printLabels()} className="rounded-sm" data-testid="ps-labels-boxes" autoFocus />
                <p className="text-[11px] text-slate-500">One 4×6 label per box ("Box 1 of 3"), each on its own letter page, ready to cut out.</p>
                <Button size="sm" onClick={printLabels} className="w-full rounded-sm bg-slate-900" data-testid="ps-labels-print">Print labels</Button>
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="sm" onClick={() => download('pdf')} className="rounded-sm" data-testid="ps-export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={() => download('json')} className="rounded-sm" data-testid="ps-export-json-btn"><Download className="w-3.5 h-3.5 mr-1" /> JSON</Button>
            <Button variant="outline" size="sm" onClick={() => download('csv')} className="rounded-sm" data-testid="ps-export-csv-btn"><Download className="w-3.5 h-3.5 mr-1" /> CSV</Button>
            <Button variant="outline" size="sm" onClick={emailPs} className="rounded-sm" data-testid="ps-email-btn"><Send className="w-3.5 h-3.5 mr-1" /> Email</Button>
            <CommercialInvoiceButton sourceType="ps" sourceId={ps.id} testId="ps-ci-btn" />
            {ps.status === 'Draft' && <Button size="sm" onClick={() => act('issue')} disabled={acting} className="bg-blue-600 hover:bg-blue-700 rounded-sm" data-testid="ps-issue-btn"><Check className="w-3.5 h-3.5 mr-1" /> Issue</Button>}
            {ps.status === 'Issued' && <Button size="sm" onClick={() => act('ship')} disabled={acting} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="ps-ship-btn"><Check className="w-3.5 h-3.5 mr-1" /> Mark Shipped</Button>}
            {['Issued', 'Shipped'].includes(ps.status) && <Button size="sm" variant="outline" onClick={() => navigate(`/orders/bol/new?from_ps=${ps.id}`)} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 rounded-sm" data-testid="ps-spawn-bol-btn">⇢ Create Bill of Lading</Button>}
            {ps.status === 'Shipped' && <Button size="sm" onClick={() => act('close')} disabled={acting} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ps-close-btn"><Check className="w-3.5 h-3.5 mr-1" /> Close</Button>}
            <DocOverrideActions docType="packing-slips" docId={id} label="Packing Slip" number={ps.ps_number} status={ps.status} cancelable={['Draft', 'Issued', 'Shipped'].includes(ps.status)} testPrefix="ps" onAction={act} onReload={load} onDeleted={() => navigate('/orders/packing')} />
          </div>
        )}
      </div>

      {!isNew && <DocumentChainStrip api={api} docType="packing_slip" docId={id} />}
      {ps && !editable && <LockedDocBanner doc={ps} label="Packing Slip" onDuplicate={() => navigate(`/orders/packing/new?dup=${id}`)} />}

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Header</CardTitle></CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2">
            <Label>Customer *</Label>
            <div className="flex gap-2">
              <div className="flex-1">
            <SearchPicker value={form.customer_id || ''} options={customerOptions(customers)} onChange={(v) => setForm({ ...form, customer_id: v || '' })} disabled={!editable} placeholder="Search customers…" testId="ps-customer-select" emptyText="No customer found — add new" />
              </div>
              <QuickAddCustomerButton api={api} disabled={!editable} testId="ps-new-customer" onCreated={(c) => { setCustomers(prev => [...prev, c].sort((a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''))); setForm(f => ({ ...f, customer_id: c.id })); }} />
            </div>
          </div>
          <div><Label>Ship Date</Label><Input type="date" value={form.ship_date} onChange={(e) => setForm({ ...form, ship_date: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="ps-ship-date" /></div>
          <div><Label>Freight Terms</Label><Input value={form.freight_terms} onChange={(e) => setForm({ ...form, freight_terms: e.target.value })} placeholder="e.g. FOB Origin, Prepaid" disabled={!editable} className="rounded-sm" data-testid="ps-freight-terms" /></div>
          <div><Label>Carrier</Label>
            <CarrierSelect
              value={form.carrier}
              onChange={(v) => setForm({ ...form, carrier: v })}
              onPickRich={(c) => setForm(prev => ({
                ...prev,
                carrier: c.name,
                freight_terms: c.default_freight_terms || prev.freight_terms,
              }))}
              disabled={!editable}
              testid="ps-carrier"
            />
          </div>
          <div><Label>Tracking #</Label><Input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="ps-tracking" /></div>
          <div><Label>Shipping Method</Label><Input value={form.shipping_method} onChange={(e) => setForm({ ...form, shipping_method: e.target.value })} placeholder="Ground, Overnight, LTL…" disabled={!editable} className="rounded-sm" data-testid="ps-shipping-method" /></div>
          <div><Label>Customer PO #</Label><Input value={form.customer_po_reference} onChange={(e) => setForm({ ...form, customer_po_reference: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="ps-customer-po" /></div>
          <div><Label># Packages</Label><Input type="number" min="0" value={form.package_count} onChange={(e) => setForm({ ...form, package_count: e.target.value })} disabled={!editable} className="rounded-sm mono text-right" data-testid="ps-package-count" /></div>
          <div><Label>Total Weight (lbs)</Label><Input type="number" min="0" step="0.01" value={form.total_weight_lbs} onChange={(e) => setForm({ ...form, total_weight_lbs: e.target.value })} disabled={!editable} className="rounded-sm mono text-right" data-testid="ps-total-weight" /></div>
          <div className="col-span-2 md:col-span-4"><Label>Ship From Address</Label><Textarea rows={2} value={form.ship_from_address} onChange={(e) => setForm({ ...form, ship_from_address: e.target.value })} placeholder="Overrides default facility address on the printed slip" disabled={!editable} className="rounded-sm" data-testid="ps-ship-from" /></div>
          <div className="col-span-2 md:col-span-4"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="ps-notes" /></div>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Line Items</CardTitle>
          {editable && <Button size="sm" variant="outline" onClick={addLine} className="rounded-sm" data-testid="ps-add-line-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Line</Button>}
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Item SKU</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Description *</TableHead>
              <TableHead className="text-right">Qty Ordered</TableHead>
              <TableHead className="text-right">Qty Shipped</TableHead>
              <TableHead>UOM</TableHead>
              <TableHead>Lot / Serial</TableHead>
              {editable && <TableHead></TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {form.lines.map((ln, i) => (
                <TableRow key={ln.id} data-testid={`ps-line-${i}`}>
                  <TableCell className="min-w-[160px]">
                    <SearchPicker value={ln.item_sku || ''} options={itemOptions} onChange={(v) => fillLineFromItem(i, v)} disabled={!editable || !!ln.product_type_id} placeholder="— pick item —" testId={`ps-line-item-${i}`} />
                  </TableCell>
                  <TableCell className="min-w-[160px]">
                    <SearchPicker value={ln.product_type_id || ''} options={productOptions} onChange={(v) => fillLineFromProduct(i, v)} disabled={!editable || !!ln.item_sku} placeholder="— pick product —" testId={`ps-line-product-${i}`} />
                  </TableCell>
                  <TableCell className="min-w-[220px]"><Input value={ln.description} onChange={(e) => updateLine(i, { description: e.target.value })} disabled={!editable} className="rounded-sm text-xs" data-testid={`ps-line-desc-${i}`} /></TableCell>
                  <TableCell className="text-right w-24"><Input type="number" min="0" step="0.01" value={ln.quantity_ordered} onChange={(e) => updateLine(i, { quantity_ordered: e.target.value })} disabled={!editable} className="rounded-sm text-xs text-right mono" data-testid={`ps-line-qty-ordered-${i}`} /></TableCell>
                  <TableCell className="text-right w-24"><Input type="number" min="0" step="0.01" value={ln.quantity_shipped} onChange={(e) => updateLine(i, { quantity_shipped: e.target.value })} disabled={!editable} className="rounded-sm text-xs text-right mono" data-testid={`ps-line-qty-shipped-${i}`} /></TableCell>
                  <TableCell className="w-16"><Input value={ln.uom || ''} onChange={(e) => updateLine(i, { uom: e.target.value })} disabled={!editable} className="rounded-sm text-xs" /></TableCell>
                  <TableCell className="w-32"><Input value={ln.lot_or_serial || ''} onChange={(e) => updateLine(i, { lot_or_serial: e.target.value })} disabled={!editable} className="rounded-sm text-xs mono" data-testid={`ps-line-lot-${i}`} /></TableCell>
                  {editable && <TableCell className="w-10"><Button variant="ghost" size="icon" onClick={() => removeLine(i)} className="text-red-600 h-7 w-7"><X className="w-3.5 h-3.5" /></Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editable && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ps-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : (isNew ? 'Create Draft' : 'Save Changes')}
          </Button>
        </div>
      )}
      {kitFor && <ProductKitDialog product={kitFor.product} items={items} unitQty={kitFor.qty} onConfirm={addKitLines} onClose={() => setKitFor(null)} />}
      {!isNew && ps && <DocHistoryPanel entityType="PackingSlip" docId={id} refreshKey={`${ps.status}-${ps.updated_at}`} />}
    </div>
  );
};

export default PackingSlipDetailPage;
